import React, {useEffect} from "react";
import styled, {css, keyframes} from "styled-components";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {getIsPromoBannerOpen, getPromoBanner} from "modules/selectors";
import {closePromoBanner, fetchSettings} from "modules/actions";
import {PrimaryButton, PureButton} from "components/Button";
import {ReactComponent as Close} from "assets/images/icons/diamondClose.svg";
import qatarLogo from "assets/images/logos/qatar_logo.png";
import qatarLogoMobile from "assets/images/logos/qatar_logo_mobile.png";
import qatarLogoCocaCola from "assets/images/logos/qatar_logo_cocacola.svg";
import fifaPlusLogo from "assets/images/logos/fifa_plus_logo_new.svg";
import {
	ADOBE_GAME_NAME,
	HEADER_HEIGHT,
	HEADER_HEIGHT_LD,
	HEADER_HEIGHT_TB,
	PromoName,
	PROMO_BANNER,
	trackAdobeAnalyticsPure,
} from "modules";
import {media} from "assets/css/media";
import Cookies from "js-cookie";
import {chain, get, identity} from "lodash";
import {useLocation} from "react-router-dom";

const slideIn = keyframes`
	from {
		transform: translateX(-100%);
	}
	to {
		transform: translateX(0);
	}
`;

const Wrapper = styled.a`
	width: calc(100% - 40px);
	display: flex;
	min-height: 70px;
	z-index: 9;
	position: fixed;
	top: ${HEADER_HEIGHT + 20}px;
	border-radius: 8px;
	overflow: hidden;
	left: 20px;
	animation: ${slideIn} 0.4s ease-in-out forwards;
	box-shadow: 0 0 4px #222;

	@media (max-width: ${media.large_desktop}) {
		top: ${HEADER_HEIGHT_LD + 20}px;
	}

	@media (max-width: ${media.tablet}) {
		top: ${HEADER_HEIGHT_TB + 20}px;
	}
`;

const LogoWrapper = styled.div`
	background: #550065;
	display: flex;
	align-items: center;
	padding-left: 60px;
	padding-right: 20px;

	@media (max-width: ${media.mobile}) {
		padding-left: 10px;
		padding-right: 10px;
	}
`;

const Logo = styled.img`
	@media (max-width: ${media.mobile}) {
		display: none;
	}
`;

const LogoMobile = styled.img`
	display: none;
	@media (max-width: ${media.mobile}) {
		display: inline;
	}
`;

const LogoWrapperFifa = styled(LogoWrapper)`
	background: linear-gradient(90deg, #00b8ff 0%, #0a84ff 100%);

	@media (max-width: ${media.mobile}) {
		img {
			width: 50px;
		}
	}
`;

const LogoWrapperCocaCola = styled(LogoWrapper)`
	background: #f10000;

	@media (max-width: ${media.mobile}) {
		img {
			width: 60px;
		}
	}
`;

interface IContentProps {
	$backgroundColor: string;
	$backgroundImage?: string;
	$color: string;
}

const getBackgroundImageStyles = ({$backgroundImage}: IContentProps) =>
	$backgroundImage &&
	css`
		background-image: url(${$backgroundImage});
		background-position: bottom right;
		background-repeat: no-repeat;
	`;

const Content = styled.div<IContentProps>`
	background-color: ${({$backgroundColor}) => $backgroundColor};
	${getBackgroundImageStyles};
	color: ${({$color}) => $color};
	font-family: "Qatar2022", sans-serif;
	font-size: 16px;
	line-height: 140%;
	flex: 1;
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 8px;
	padding: 10px;
	padding-right: 60px;

	@media (max-width: ${media.mobile}) {
		font-size: 12px;
		padding: 12px;
		padding-right: 15px;
	}
`;

const Title = styled.h6`
	font-weight: 400;
`;

const Description = styled.p`
	font-weight: 500;
`;

const ButtonsWrapper = styled.div`
	display: flex;
	align-items: center;
	gap: 24px;

	svg {
		vertical-align: middle;
	}
`;

interface IButtonProps {
	$backgroundColor: string;
	$hoverColor: string;
	$color: string;
}

const StyledPrimaryButton = styled(PrimaryButton)<IButtonProps>`
	background: ${({$backgroundColor}) => $backgroundColor};
	color: ${({$color}) => $color};

	&:after,
	&:before {
		background: ${({$backgroundColor}) => $backgroundColor};
		border-color: ${({$backgroundColor}) => $backgroundColor};
	}

	&:hover:not([disabled]):after,
	&:hover:not([disabled]):before {
		background: ${({$hoverColor}) => $hoverColor};
		border-color: ${({$hoverColor}) => $hoverColor};
	}

	&:hover:not([disabled]) {
		background: ${({$hoverColor}) => $hoverColor};
	}

	@media (max-width: ${media.mobile}) {
		display: none;
	}
`;

interface IBannerProps {
	banner: PromoName;
}

const PromoBannerComponent: React.FC<IBannerProps> = ({banner}) => {
	const {t, i18n} = useTranslation();
	const dispatch = useDispatch();
	const {pathname} = useLocation();
	const path = chain(pathname).split("/").filter(identity).pop().defaultTo("").value();
	const hasSeenBanner = Cookies.get(`${banner}_promo_banner`) === "true";
	const isCocaCola = banner === PromoName.CocaCola;
	const isFifaLogo = [PromoName.Brackets, PromoName.Trivia, PromoName.FIFABestAwards].includes(
		banner
	);
	const {
		backgroundColor,
		backgroundImage,
		textColor,
		buttonBackground,
		buttonColor,
		buttonHover,
		buttonLink,
		buttonText,
		title,
		description,
	} = get(PROMO_BANNER, banner);

	const link = isCocaCola
		? `https://www.fifa.com/fifaplus/${i18n.language}/articles/qatar2022-notifications-multiple-teams-${i18n.language}?intcmp=(p_banner)(ch_playzone)(c_fifaplus)(sc_worldcup)(ssc_matchnotifications)`
		: buttonLink;

	const setBannerViewed = () => {
		Cookies.set(`${banner}_promo_banner`, "true", {
			expires: 1,
		});
	};

	const closeBanner = (e: React.SyntheticEvent<HTMLButtonElement>) => {
		e.stopPropagation();
		e.preventDefault();

		trackAdobeAnalyticsPure({
			event: "assetClick",
			asset: `${ADOBE_GAME_NAME}:banner`,
			pageName: `worldcup:qatar2022:games:${ADOBE_GAME_NAME}:${path}`,
		});

		setBannerViewed();
		dispatch(closePromoBanner());
	};

	if (hasSeenBanner) {
		return null;
	}

	return (
		<Wrapper href={link} onClick={setBannerViewed}>
			{isFifaLogo ? (
				<LogoWrapperFifa>
					<img src={fifaPlusLogo} width="99" alt="FIFA+" />
				</LogoWrapperFifa>
			) : isCocaCola ? (
				<LogoWrapperCocaCola>
					<img
						src={qatarLogoCocaCola}
						width="94"
						alt="Coca Cola FIFA World Cup Qatar 2022"
					/>
				</LogoWrapperCocaCola>
			) : (
				<LogoWrapper>
					<Logo src={qatarLogo} width="113" alt="FIFA World Cup Qatar 2022" />
					<LogoMobile src={qatarLogoMobile} width="47" alt="FIFA World Cup Qatar 2022" />
				</LogoWrapper>
			)}
			<Content
				$backgroundColor={backgroundColor}
				$backgroundImage={backgroundImage}
				$color={textColor}>
				<div>
					<Title>{t(title)}</Title>
					<Description>{t(description)}</Description>
				</div>

				<ButtonsWrapper>
					<StyledPrimaryButton
						$backgroundColor={buttonBackground}
						$color={buttonColor}
						$hoverColor={buttonHover}>
						{t(buttonText)}
					</StyledPrimaryButton>

					<PureButton onClick={closeBanner}>
						<Close />
					</PureButton>
				</ButtonsWrapper>
			</Content>
		</Wrapper>
	);
};

export const PromoBanner: React.FC = () => {
	const dispatch = useDispatch();
	const banner = useSelector(getPromoBanner);
	const isOpen = useSelector(getIsPromoBannerOpen);

	useEffect(() => {
		dispatch(fetchSettings());
	}, [dispatch]);

	if (!banner) {
		return null;
	}

	if (!isOpen) {
		return null;
	}

	return <PromoBannerComponent banner={banner} />;
};
